<template>
  <v-flex
    class="py-1"
  >
    <v-list-item
      :id="'processing-result-' + tileName"
      :value="tileName"
      class="text-truncate"
      :disabled="isItemDisabled"
    >
      <v-list-item-icon>
        <v-icon>mdi-image</v-icon>
      </v-list-item-icon>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-list-item-content v-on="on" @click="itemSelected(tileName)">
            {{tileName | displayName}}
          </v-list-item-content>
        </template>
        <span>{{tileName | displayName}}</span>
      </v-tooltip>
    </v-list-item>
    <v-expand-transition>
      <div
        v-if="isHistogramAvailable"
        :id="tileName + '-histogram'"
      >
        <v-sparkline-horus
          fill
          auto-draw
          height="150px"
          smooth
          :gradient="histogramCMap"
          gradient-direction="right"
          :value="histGraph"
          :labels="labels"
          :init-gradient="initGradient"
          :final-gradient="finalGradient"
          show-labels
          label-size="10"
          padding="20"
        />
        <v-range-slider
          v-model="minMax"
          :id.prop="`${tileName}-range-slider`"
          :min="statsMinNormalized"
          :max="statsMaxNormalized"
          hide-details
          color="accent"
          track-color="primary"
        />
        <v-row
          class="ma-0"
        >
          <v-spacer/>
          <v-tooltip right>
            <template v-slot:activator="{ on }">
              <v-btn
                :loading="isSavingSettings"
                :id="tileName + '-histogram-save-config'"
                icon
                color="success"
                @click="onSaveClick"
                outlined
                class="mx-1 mt-4"
                v-on="on"
              >
                <v-icon>mdi-content-save</v-icon>
              </v-btn>
            </template>
            <span>Salvar configurações</span>
          </v-tooltip>
        </v-row>
      </div>
    </v-expand-transition>
  </v-flex>
</template>

<script>
  import {find, includes, map} from "lodash/collection"
  import {gte, isEmpty, isEqual, eq} from "lodash/lang"
  import {ProcessingsTilesEventBus} from "../../../helpers/mapview/events"
  import VSparklineHorus from "../../Helpers/Vuetify/VSparkline"
  import {COLOR_TABLES, TILE_TYPE_NAMES} from "../../../helpers/mapview/constants"
  import {addUpdHistogramConfig} from "../../../api/api-tile"
  import {createEventInternal} from "../../../api/api-project"
  import {NOTIFICATION_TYPES, NotificationOptions} from "../../../helpers/notify"

  const _Map = Symbol.for("_Map")
  const _Processing = Symbol.for("_Processing")

  const normalizeValue = (value, min, max) => {
    return (
      (value - min) /
      (max - min)
    ) * 100
  }

  const denormalizeValue = (value, min, max) => {
    return ((value / 100) * (max - min)) + min
  }

  export default {
    name: "ProcessingResultItem",
    components: {VSparklineHorus},
    inject: {_Map, _Processing},
    props: {
      processing: {
        type: Object,
        required: true
      },
      selected: {
        type: Array,
        required: true
      },
      tileName: {
        type: String,
        required: true
      },
      rasterName: {
        type: String,
        required: false
      },
      totalBands: {
        type: Number,
        default: 0
      },
      statsMin: {
        type: Number,
        required: false,
      },
      statsMax: {
        type: Number,
        required: false,
      },
      min: {
        type: Number,
        required: false,
      },
      max: {
        type: Number,
        required: false,
      },
      histogramGraph: {
        type: Array,
        required: false
      },
      showHistogram: {
        type: Boolean,
        default: false
      }
    },
    data: () => ({
      isSavingSettings: false,
      isGeneratingReport: false,
      minMax: [],
    }),
    methods: {
      retiraAcento(palavra){  
        var com_acento = 'áàãâäéèêëíìîïóòõôöúùûüçÁÀÃÂÄÉÈÊËÍÌÎÏÓÒÕÖÔÚÙÛÜÇ´`^¨~- ';    
        var sem_acento = 'aaaaaeeeeiiiiooooouuuucAAAAAEEEEIIIIOOOOOUUUUC     __';  
        let l; let l2;
        for (l in palavra){  
          for (l2 in com_acento){  
            if (palavra[l] == com_acento[l2]){  
              palavra=palavra.replace(palavra[l],sem_acento[l2]);  
            }  
          }  
        }
        palavra = palavra.replace('__', '_')
        return palavra.toUpperCase();  
      },       
      itemSelected(tileName) {
        let event_name = this.retiraAcento(tileName)
        event_name = 'VIEWED_' + event_name

        const authToken = this.$apiAuth.getAuthToken()

        let data_send = {
          "event_name": event_name.replace('__', '_'),
          "cd_processing": this.processing["id_processing"]
        }
        createEventInternal(authToken, data_send)
        .then(() => {
          console.log("INTERNAL EVENT CREATED")
        })
        .catch(() => {
          console.log("ERROR CREATE INTERNAL EVENT")
        })
      },
      onCreated() {
        if (eq(this.totalBands, 1))
          this.minMax = [
            normalizeValue(this.min, this.statsMin, this.statsMax),
            normalizeValue(this.max, this.statsMin, this.statsMax)
          ]
      },
      onSaveClick() {
        const histogramConfigData = {
          ab_name: this.rasterName,
          cd_processing: this.processing["id_processing"],
          fl_min_val: denormalizeValue(this.minMax[0], this.statsMin, this.statsMax),
          fl_max_val: denormalizeValue(this.minMax[1], this.statsMin, this.statsMax),
        }
        this.isSavingSettings = true
        addUpdHistogramConfig(this.$apiAuth.getAuthToken(), histogramConfigData)
          .then(() => {
            this.$notify(new NotificationOptions({
              message: "Suas modificações foram salvas!",
              type: NOTIFICATION_TYPES.SUCCESS
            }))
          })
          .finally(() => {
            this.isSavingSettings = false
          })
      }
    },
    filters: {
      displayName(value) {
        let displayValue = TILE_TYPE_NAMES[value]
        if (!displayValue) return ""
        return displayValue
      }
    },
    computed: {
      Map() {
        return this._Map()
      },
      Processing() {
        return this._Processing()
      },
      isItemDisabled() {
        if (!this.Map) return true
        const obj = find(
          this.Map.sideBySide.idleLayers,
          item => {
            return (
              isEqual(item["processing"]["id_processing"], this.processing["id_processing"]) &&
              isEqual(item["tileName"], this.tileName)
            )
          }
        )
        return (gte(this.Map.sideBySide.idleLayers.length, 2) || (this.selected.length == 2)) && Boolean(!obj)
      },
      isHistogramAvailable() {
        if (!this.showHistogram) return false
        if (isEmpty(this.selected)) return false
        if (!this.histogramGraph) return false

        return (
          includes(this.selected, this.tileName) &&
          eq(this.totalBands, 1)
        )
      },
      histogramCMap() {
        if (!this.rasterName) return []
        const cmap = COLOR_TABLES[this.rasterName]
        if (!cmap) return []
        return cmap
      },
      histGraph() {
        if (!this.showHistogram) return []
        if (!this.histogramGraph) return []
        return map(this.histogramGraph, x => x["frequency"])
      },
      labels() {
        if (!this.showHistogram) return []
        if (!this.histogramGraph) return []
        return map(this.histogramGraph, x => parseFloat(x["pixel_value"]).toFixed(2))
      },
      statsMinNormalized() {
        return normalizeValue(this.statsMin, this.statsMin, this.statsMax)
      },
      statsMaxNormalized() {
        return normalizeValue(this.statsMax, this.statsMin, this.statsMax)
      },
      initGradient() {
        return this.minMax[0] / 100
      },
      finalGradient() {
        return this.minMax[1] / 100
      }
    },
    created() {
      this.onCreated()
    },
    updated() {
      if (isEmpty(this.minMax)) return
        const thereWillBeGnomes = map(this.minMax, x => denormalizeValue(x, this.statsMin, this.statsMax))
          ProcessingsTilesEventBus.$emit("update", {
            processing: this.processing,
            tileName: this.tileName,
            minMax: thereWillBeGnomes
          })
    },
    watch: {
      minMax: {
        handler(newValue) {
          const thereWillBeGnomes = map(newValue, x => denormalizeValue(x, this.statsMin, this.statsMax))
          ProcessingsTilesEventBus.$emit("update", {
            processing: this.processing,
            tileName: this.tileName,
            minMax: thereWillBeGnomes
          })
        },
        immediate: true,
        deep: true
      },
    }
  }
</script>

<style scoped lang="scss">
</style>
